import { isSampleTypeSync } from "../../utils/misc";
import { isCompleteExpression } from "../../utils/dimensionHelpers";
import { legacyPortalIdToBreadboxGivenId } from "../../utils/slice-id";
import {
  DataExplorerContextV2,
  DataExplorerDatasetDescriptor,
  DimensionKey,
  FilterKey,
  PartialDataExplorerPlotConfig,
} from "@depmap/types";
import { isCompletePlot } from "./validation";
import { CURRENT_PLOT_VERSION } from "./plot-version";

type Datasets = Record<string, DataExplorerDatasetDescriptor[]>;

// Legacy contexts renamed `dimension_type` to `context_type` and did not have
// a `vars` property (variables were interpreted as raw slice ID strings
// instead of named references to SliceQuery objects).
export type LegacyDataExplorerContext = {
  name: string;
  context_type: string;
  expr: Record<string, any> | boolean;
};

const findDataset = (
  dataset_id: string,
  datasetList: DataExplorerDatasetDescriptor[]
) => {
  return datasetList.find((d) => {
    return (
      d.id === dataset_id ||
      d.given_id === dataset_id ||
      d.given_id === legacyPortalIdToBreadboxGivenId(dataset_id)
    );
  });
};

const makeDatasetParser = (dimensionKey: DimensionKey) => (
  partialPlot: PartialDataExplorerPlotConfig,
  dataset_id: string
) => {
  const p = { ...partialPlot };

  p.dimensions = p.dimensions || {};
  p.dimensions[dimensionKey] = p.dimensions[dimensionKey] || {};
  p.dimensions[dimensionKey]!.dataset_id = dataset_id;

  return p;
};

// Passing `xFeature` or `yFeature` exist only for backward compatibility with
// Data Explorer 1. Therefore, the index_type defaults to "depmap_model" (this
// was always the case in Data Explorer 1).
const makeFeatureParser = (dimensionKey: DimensionKey) => (
  partialPlot: PartialDataExplorerPlotConfig,
  feature: string,
  allParams: qs.ParsedQs,
  datasets: Datasets
) => {
  const p = { ...partialPlot };

  let dataset_id = (dimensionKey === "x"
    ? allParams.xDataset
    : allParams.yDataset) as string;

  dataset_id = dataset_id?.replace("breadbox/", "");

  if (!dataset_id) {
    return partialPlot;
  }

  const dataset = findDataset(dataset_id, datasets.depmap_model);
  const slice_type = dataset ? dataset.slice_type : "custom";

  p.dimensions = p.dimensions || {};
  p.dimensions[dimensionKey] = p.dimensions[dimensionKey] || {};

  p.dimensions[dimensionKey]!.dataset_id = dataset_id;
  p.dimensions[dimensionKey]!.axis_type = "raw_slice";
  p.dimensions[dimensionKey]!.slice_type = slice_type;
  p.dimensions[dimensionKey]!.aggregation = "first";
  p.dimensions[dimensionKey]!.context = {
    name: feature,
    context_type: slice_type,
    expr: { "==": [{ var: "entity_label" }, feature] } as object,
  };

  return p;
};

const makeSampleParser = (dimensionKey: DimensionKey) => (
  partialPlot: PartialDataExplorerPlotConfig,
  feature: string,
  allParams: qs.ParsedQs,
  datasets: Datasets
) => {
  const p = { ...partialPlot };

  let dataset_id = (dimensionKey === "x"
    ? allParams.xDataset
    : allParams.yDataset) as string;

  dataset_id = dataset_id?.replace("breadbox/", "");

  if (!dataset_id) {
    return partialPlot;
  }

  let dataset: Datasets[string][number] | undefined;

  Object.keys(datasets).forEach((index_type) => {
    const matchingDataset = findDataset(dataset_id, datasets[index_type]);

    const isSampleType =
      (matchingDataset?.sample_type_name !== undefined &&
        matchingDataset.sample_type_name === matchingDataset.index_type) ||
      // TODO: Remove this. For legacy support only.
      isSampleTypeSync(index_type);

    if (matchingDataset && isSampleType) {
      dataset = matchingDataset;
    }
  });

  const slice_type = dataset ? dataset.index_type : "custom";

  p.dimensions = p.dimensions || {};
  p.dimensions[dimensionKey] = p.dimensions[dimensionKey] || {};

  p.dimensions[dimensionKey]!.dataset_id = dataset_id;
  p.dimensions[dimensionKey]!.axis_type = "raw_slice";
  p.dimensions[dimensionKey]!.slice_type = slice_type;
  p.dimensions[dimensionKey]!.aggregation = "first";
  p.dimensions[dimensionKey]!.context = {
    name: feature,
    context_type: slice_type,
    expr: { "==": [{ var: "entity_label" }, feature] } as object,
  };

  return p;
};

const isSingleSlice = (context: {
  expr: LegacyDataExplorerContext["expr"];
}) => {
  const { expr } = context;

  return (
    expr !== null &&
    typeof expr === "object" &&
    "==" in expr &&
    "var" in expr["=="][0] &&
    expr["=="][0].var === "entity_label"
  );
};

const makeContextParser = (dimensionKey: DimensionKey) => (
  partialPlot: PartialDataExplorerPlotConfig,
  encodedContext: string
) => {
  const p = { ...partialPlot };
  let context: DataExplorerContextV2;

  try {
    context = JSON.parse(decodeURIComponent(encodedContext));
  } catch (e) {
    throw new Error(`Invalid context`);
  }

  const asLegacyContext = (context as unknown) as LegacyDataExplorerContext;

  if (
    !context.name ||
    !isCompleteExpression(context.expr) ||
    ![context.dimension_type, asLegacyContext.context_type].some(Boolean)
  ) {
    throw new Error("Invalid context");
  }

  p.dimensions = p.dimensions || {};
  p.dimensions[dimensionKey] = p.dimensions[dimensionKey] || {};

  p.dimensions[dimensionKey]!.slice_type =
    context.dimension_type || asLegacyContext.context_type;
  p.dimensions[dimensionKey]!.context = context;

  if (isSingleSlice(context)) {
    p.dimensions[dimensionKey]!.axis_type = "raw_slice";
    p.dimensions[dimensionKey]!.aggregation = "first";
  } else {
    p.dimensions[dimensionKey]!.axis_type = "aggregated_slice";
    // TODO: Allow this default to be overriden.
    p.dimensions[dimensionKey]!.aggregation = "mean";
  }

  return p;
};

const makeFilterParser = (filterKey: FilterKey) => (
  partialPlot: PartialDataExplorerPlotConfig,
  contextOrHash: string
) => {
  const p = { ...partialPlot };

  try {
    const parsed = JSON.parse(decodeURIComponent(contextOrHash));
    p.filters = p.filters || {};
    (p.filters[filterKey] as any) = parsed;
  } catch (e) {
    const hash = contextOrHash.replace("not_", "");
    const negated = contextOrHash.startsWith("not_");

    p.filters = p.filters || {};
    (p.filters[filterKey] as any) = { hash, negated };
  }

  return p;
};

const makeMetadataParser = (metadataKey: string) => (
  partialPlot: PartialDataExplorerPlotConfig,
  slice_id: string
) => {
  const p = { ...partialPlot };

  p.metadata = p.metadata || {};
  p.metadata[metadataKey] = { slice_id };

  return p;
};

const makeRegressionLineParser = () => (
  partialPlot: PartialDataExplorerPlotConfig,
  value: string
) => {
  const p = { ...partialPlot };

  if (value === "true") {
    p.show_regression_line = true;
  }

  return p;
};

const makeColorDatasetParser = () => (
  partialPlot: PartialDataExplorerPlotConfig,
  colorDataset: string,
  allParams: qs.ParsedQs
) => {
  const e = window.encodeURIComponent;
  const colorFeature = allParams.colorFeature as string;

  if (!colorFeature) {
    return {};
  }

  const slice_id = `slice/${e(colorDataset)}/${e(colorFeature)}/label`;

  return makeMetadataParser("color_property")(partialPlot, slice_id);
};

const parsers = {
  xDataset: makeDatasetParser("x"),
  yDataset: makeDatasetParser("y"),

  xFeature: makeFeatureParser("x"),
  yFeature: makeFeatureParser("y"),

  xSample: makeSampleParser("x"),
  ySample: makeSampleParser("y"),

  xContext: makeContextParser("x"),
  yContext: makeContextParser("y"),

  color1: makeFilterParser("color1"),
  color2: makeFilterParser("color2"),
  filter: makeFilterParser("visible"),

  color_property: makeMetadataParser("color_property"),
  regressionLine: makeRegressionLineParser(),

  colorDataset: makeColorDatasetParser(),
};

const parse = (params: qs.ParsedQs, datasets: Datasets) => {
  let plot: PartialDataExplorerPlotConfig | null = null;

  Object.entries(parsers).forEach(([param, parser]) => {
    const value = params[param];

    if (typeof value === "string") {
      plot = parser(plot || {}, value, params, datasets);
    }
  });

  return plot as PartialDataExplorerPlotConfig | null;
};

const inferIndexType = (
  partialPlot: PartialDataExplorerPlotConfig,
  datasets: Datasets
) => {
  let dataset_id = partialPlot.dimensions?.x?.dataset_id;
  const slice_type = partialPlot.dimensions?.x?.slice_type;

  if (!dataset_id || slice_type === undefined) {
    return null;
  }

  if (slice_type === "custom" || slice_type === null) {
    return "depmap_model";
  }

  dataset_id = dataset_id.replace("breadbox/", "");
  const dataset = findDataset(dataset_id, datasets[slice_type]);

  return dataset?.slice_type || null;
};

const inferPlotType = (partialPlot: PartialDataExplorerPlotConfig) => {
  if (!partialPlot.dimensions) {
    return null;
  }

  return Object.keys(partialPlot.dimensions).length === 1
    ? "density_1d"
    : "scatter";
};

// Legacy `color_property` (or `colorDataset` + `colorFeature`) on a 1D plot
// meant "one violin track per category, and color those tracks" — under
// version 1 there was a single axis and it drove coloring and faceting at
// once. Expressing that same intent post-ADR-0004/0005 takes the two-axis
// form: the property backs `facet_by`, and `color_by` defers to it via the
// "facet" sentinel.
//
// That translation already exists as the v1 -> v2 migration in
// readPlotFromQueryString, but this is a MINT POINT that stamps version 2, so
// the migration is (correctly) skipped here and the shape has to be minted
// post-migration instead. Nothing else will do it for us.
//
// Scoped to density_1d deliberately. On a scatter, a real `facet_by` routes to
// SmallMultiplesScatter — legacy `color_property` there only ever colored the
// points, it never exploded them into a panel grid — so scatter keeps
// `color_by: "property"` and no `facet_by`.
//
// The rename is safe only because makePlotConfigBreadboxModeCompatible
// resolves `facet_property` slice IDs on the same footing as `color_property`
// (see the `axis` lookup there). That pass, not this one, decides whether the
// property is really "property" or "custom" — so don't try to be smarter about
// the mode here.
const facetOnColorPropertyFor1d = (
  partialPlot: PartialDataExplorerPlotConfig,
  plot_type: ReturnType<typeof inferPlotType>
): PartialDataExplorerPlotConfig => {
  const { color_property, ...restMetadata } = partialPlot.metadata || {};

  if (plot_type !== "density_1d" || !color_property) {
    return partialPlot;
  }

  return {
    ...partialPlot,
    facet_by: "property",
    metadata: { ...restMetadata, facet_property: color_property },
  };
};

const inferColorBy = (partialPlot: PartialDataExplorerPlotConfig) => {
  if (partialPlot.filters?.color1 || partialPlot.filters?.color2) {
    return "aggregated_slice";
  }

  // Defer to the facet_by that facetOnColorPropertyFor1d just set, so the
  // plot is colored AND faceted off one shared partition.
  if (partialPlot.metadata?.facet_property) {
    return "facet";
  }

  if (partialPlot.metadata?.color_property) {
    return "property";
  }

  // This mint point stamps CURRENT_PLOT_VERSION (now 2), so an absent
  // color_by it emits is read back as "facet" (defer to facet_by). Reaching
  // here means there is no facet_by either (the arm above would have caught
  // it), so "nothing to color by" genuinely means uniform, never "match a
  // facet_by that isn't there." Returning "uniform" explicitly (not
  // null/absent) keeps such plots rendering exactly as they did pre-flip. See
  // ADR 0001 §7's "worked example" — this is that exact hazard.
  return "uniform";
};

// Both metadata keys are tested by name rather than by `metadata`'s own
// truthiness: the bag can now hold either color_property or facet_property,
// so bag-presence no longer implies a categorical partition exists to sort
// facets by (ADR 0002 §3's discipline — a field's meaning must not ride on an
// unrelated key's presence).
const inferSortBy = (partialPlot: PartialDataExplorerPlotConfig) => {
  const { color_property, facet_property } = partialPlot.metadata || {};

  if (color_property || facet_property) {
    return "mean_values_asc" as PartialDataExplorerPlotConfig["sort_by"];
  }

  return null;
};

const inferOtherProps = (
  partialPlot: PartialDataExplorerPlotConfig | null,
  datasets: Datasets
): PartialDataExplorerPlotConfig | null => {
  if (!partialPlot) {
    return null;
  }

  const index_type = inferIndexType(partialPlot, datasets);
  const plot_type = inferPlotType(partialPlot);

  // Must happen before color_by/sort_by are inferred: both read the metadata
  // keys this rewrites.
  const plot = facetOnColorPropertyFor1d(partialPlot, plot_type);

  const color_by = inferColorBy(plot);
  const sort_by = inferSortBy(plot);

  return {
    ...(index_type ? { index_type } : {}),
    ...(plot_type ? { plot_type } : {}),
    ...(color_by ? { color_by } : {}),
    ...(sort_by ? { sort_by } : {}),
    ...plot,
  };
};

export function hasSomeShorthandParams(params: qs.ParsedQs) {
  return Object.keys(params).some((key) => key in parsers);
}

export function omitShorthandParams(params: qs.ParsedQs) {
  const out: qs.ParsedQs = {};

  Object.keys(params).forEach((key) => {
    if (!(key in parsers)) {
      out[key] = params[key];
    }
  });

  return out;
}

// "Shorthand params" contrast with the "p" URL param that encodes an entire
// plot config object as one big, compressed blob of JSON. These shorthand
// params are human-readable and require fewer explicit parameters (at minimum,
// a plot can be derived from just `xDataset` and `xFeature`).
export function parseShorthandParams(params: qs.ParsedQs, datasets: Datasets) {
  let plot = parse(params, datasets);
  plot = inferOtherProps(plot, datasets);

  if (plot === null) {
    return null;
  }

  if (!isCompletePlot(plot)) {
    let message = "Unable to construct a plot from URL params. Problems:\n";

    if (!plot.index_type) {
      message += "- Could not infer index_type";
    }

    if (params.yDataset && !params.xDataset) {
      message += "- `yDataset` was specified without an `xDataset` \n";
    }

    if (params.xDataset && !(params.xFeature || params.xContext)) {
      message +=
        "- `xDataset` was specified without a corresponding `xFeature` or `xContext`\n";
    }

    if (params.yDataset && !(params.yFeature || params.yContext)) {
      message +=
        "- `yDataset` was specified without a corresponding `yFeature` or `yContext`\n";
    }

    if (params.xFeature && !params.xDataset) {
      message +=
        "- `xFeature` was specified without a corresponding `xDataset`\n";
    }

    if (params.yFeature && !params.yDataset) {
      message +=
        "- `yFeature` was specified without a corresponding `yDataset`\n";
    }

    if (params.xContext && !params.xDataset) {
      message +=
        "- `xContext` was specified without a corresponding `xDataset`\n";
    }

    if (params.yContext && !params.yDataset) {
      message +=
        "- `yContext` was specified without a corresponding `yDataset`\n";
    }

    if (params.colorDataset && !params.colorFeature) {
      message +=
        "- `colorDataset` was specified without a corresponding `colorFeature`\n";
    }

    throw new Error(message);
  }

  // Shorthand params are a MINT POINT, so stamp the schema version here.
  //
  // The plot built above is current-schema by construction: object `dimensions`,
  // canonical property names, and only canonical `color_by` / `axis_type` values
  // (never the legacy "entity" / "context" spellings, never `entity_type`). It is
  // therefore honestly v1, not merely untagged.
  //
  // Stamping matters because the reader coerces an absent `version` to 0 and runs
  // pre-versioning migrations against it. Leave shorthand unstamped and a link
  // generated one second from now becomes indistinguishable from a years-old
  // bookmark — the moment a Phase B migration exists, it would be applied to a
  // payload that was never minted under the old regime.
  //
  // This says nothing about backend-nativity, and must not be read as such: the
  // dimensions above still carry raw legacy dataset IDs (the rewrite is
  // deliberately deferred), context_type contexts, the "custom" slice_type
  // sentinel, and slice_id metadata. Those are repaired unconditionally
  // downstream by makePlotConfigBreadboxModeCompatible, which does not — and must
  // not — gate on version.
  return { ...plot, version: CURRENT_PLOT_VERSION };
}
